import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Link2, Copy, MousePointerClick, Users, DollarSign,
  TrendingUp, Plus, ExternalLink, UserPlus, CheckCircle, XCircle,
  Trash2, Wallet, Percent
} from 'lucide-react';
import AffiliatePayouts from './AffiliatePayouts';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function ReferralDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOrganizer = user?.role === 'organizer' || user?.role === 'admin';

  const [myLinks, setMyLinks] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [myCommissions, setMyCommissions] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventStats, setEventStats] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [eventCommissions, setEventCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Invite affiliate state
  const [inviteMethod, setInviteMethod] = useState('phone'); // 'phone' | 'email'
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteCountryCode, setInviteCountryCode] = useState('+234');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  // Deep-link from email CTA: ?event_id=...&prompt=generate
  const [searchParams] = useSearchParams();
  const promptEventId = searchParams.get('event_id');
  const promptAction = searchParams.get('prompt');
  const promptHandledRef = useRef(false);
  const [highlightedRefId, setHighlightedRefId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Active tab — when ?prompt=generate is set, force "affiliate" tab so the user sees their link list
  const initialTab = (promptAction === 'generate') ? 'affiliate' : (isOrganizer ? 'organizer' : 'affiliate');
  const [activeTab, setActiveTab] = useState(initialTab);

  // Handle ?prompt=generate&event_id=… from email CTA — runs once after data loads
  useEffect(() => {
    if (loading || promptHandledRef.current) return;
    if (promptAction !== 'generate' || !promptEventId) return;
    promptHandledRef.current = true;

    (async () => {
      // Pre-select the event (if it's in the organizer's events list)
      setSelectedEventId(promptEventId);

      // Check for existing link first
      const existing = myLinks.find(l => l.event_id === promptEventId);
      if (existing) {
        setHighlightedRefId(existing.referral_id);
        // Scroll to it
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="referral-link-${existing.ref_code}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        toast.success('Your referral link is ready below');
        return;
      }

      // No existing link → auto-trigger generation (backend returns existing idempotently if one was created concurrently)
      const created = await handleCreateLink(promptEventId, { silent: false });
      if (created) {
        setHighlightedRefId(created.referral_id);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="referral-link-${created.ref_code}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, promptAction, promptEventId, myLinks.length]);

  const fetchEventData = useCallback(async (eventId) => {
    if (!eventId) {
      setEventStats(null);
      setAffiliates([]);
      setEventCommissions([]);
      return;
    }
    try {
      const [statsRes, affRes, commRes] = await Promise.all([
        axios.get(`${API_URL}/api/referrals/event/${eventId}/stats`),
        axios.get(`${API_URL}/api/referrals/affiliates/event/${eventId}`),
        axios.get(`${API_URL}/api/referrals/commissions/event/${eventId}`)
      ]);
      setEventStats(statsRes.data);
      setAffiliates(affRes.data);
      setEventCommissions(commRes.data);
    } catch (err) {
      console.error('Error fetching event data:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId && isOrganizer) {
      fetchEventData(selectedEventId);
    } else {
      setEventStats(null);
      setAffiliates([]);
      setEventCommissions([]);
    }
  }, [selectedEventId, isOrganizer, fetchEventData]);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API_URL}/api/referrals/my-links`),
        axios.get(`${API_URL}/api/referrals/commissions/my`)
      ];
      if (isOrganizer) {
        promises.push(axios.get(`${API_URL}/api/events/organizer/my-events`));
      }
      const results = await Promise.all(promises);
      setMyLinks(results[0].data);
      setMyCommissions(results[1].data);
      if (results[2]) setMyEvents(results[2].data);
    } catch (err) {
      console.error('Error fetching referral data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async (eventId, opts = {}) => {
    if (!eventId) {
      if (!opts.silent) toast.error('Select an event first');
      return null;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/referrals/create`, { event_id: eventId });
      const isExisting = (res.data?.message || '').toLowerCase().includes('already');
      if (!opts.silent) {
        toast.success(isExisting ? 'Your existing referral link is ready' : 'Referral link created!');
      }
      const referral = res.data.referral;
      setMyLinks(prev => [referral, ...prev.filter(l => l.referral_id !== referral.referral_id)]);
      return referral;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create referral link');
      return null;
    } finally { setCreating(false); }
  };

  const handleInviteAffiliate = async () => {
    if (!selectedEventId) { toast.error('Select an event first'); return; }

    const payload = { event_id: selectedEventId };
    if (inviteMethod === 'phone') {
      if (!invitePhone.trim()) { toast.error('Enter a phone number'); return; }
      payload.phone = invitePhone.trim();
      payload.country_code = inviteCountryCode;
    } else {
      const email = inviteEmail.trim().toLowerCase();
      if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
      payload.email = email;
    }

    setInviting(true);
    try {
      const res = await axios.post(`${API_URL}/api/referrals/affiliates/invite`, payload);
      toast.success(res.data.message);
      setInvitePhone('');
      setInviteEmail('');
      fetchEventData(selectedEventId);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to invite affiliate';
      toast.error(detail);
    } finally { setInviting(false); }
  };

  const switchInviteMethod = (method) => {
    setInviteMethod(method);
    setInvitePhone('');
    setInviteEmail('');
  };

  const handleRemoveAffiliate = async (affiliateId) => {
    try {
      await axios.delete(`${API_URL}/api/referrals/affiliates/${affiliateId}`);
      toast.success('Affiliate removed');
      setAffiliates(prev => prev.filter(a => a.affiliate_id !== affiliateId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const copyLink = (refCode, eventId) => {
    navigator.clipboard.writeText(`${SITE_URL}/events/${eventId}?ref=${refCode}`);
    toast.success('Referral link copied!');
  };

  const totalClicks = myLinks.reduce((s, l) => s + (l.clicks || 0), 0);
  const totalConversions = myLinks.reduce((s, l) => s + (l.conversions || 0), 0);
  const myTotalEarned = myCommissions.reduce((s, c) => s + (c.commission_amount || 0), 0);
  const myTotalPending = myCommissions.filter(c => c.status === 'pending').reduce((s, c) => s + (c.commission_amount || 0), 0);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" data-testid="referral-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Referral Tracking</h1>
            <p className="text-muted-foreground">Manage affiliates, share events, and track commissions</p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10"><Link2 className="w-5 h-5 text-blue-500" /></div>
                <div>
                  <p className="text-2xl font-bold" data-testid="total-links">{myLinks.length}</p>
                  <p className="text-xs text-muted-foreground">My Links</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10"><MousePointerClick className="w-5 h-5 text-amber-500" /></div>
                <div>
                  <p className="text-2xl font-bold" data-testid="total-clicks">{totalClicks}</p>
                  <p className="text-xs text-muted-foreground">Total Clicks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10"><Users className="w-5 h-5 text-green-500" /></div>
                <div>
                  <p className="text-2xl font-bold" data-testid="total-conversions">{totalConversions}</p>
                  <p className="text-xs text-muted-foreground">Conversions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10"><Wallet className="w-5 h-5 text-purple-500" /></div>
                <div>
                  <p className="text-2xl font-bold" data-testid="my-total-earned">{myTotalEarned.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    Earned {myTotalPending > 0 && <span className="text-amber-500">({myTotalPending.toLocaleString()} pending)</span>}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            {isOrganizer && <TabsTrigger value="organizer" data-testid="tab-organizer">Organizer</TabsTrigger>}
            <TabsTrigger value="affiliate" data-testid="tab-affiliate">My Referrals</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings">My Earnings</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
          </TabsList>

          {/* ===== ORGANIZER TAB ===== */}
          {isOrganizer && (
            <TabsContent value="organizer" className="space-y-6">
              {/* Event Selector */}
              <Card>
                <CardHeader><CardTitle className="text-lg">Select Event</CardTitle></CardHeader>
                <CardContent>
                  <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                    <SelectTrigger data-testid="referral-event-select"><SelectValue placeholder="Choose an event to manage" /></SelectTrigger>
                    <SelectContent>
                      {myEvents.map(e => (
                        <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {selectedEventId && eventStats && (
                <>
                  {/* Commission Config Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Percent className="w-5 h-5" /> Commission Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{eventStats.commission_percent || 0}%</p>
                          <p className="text-xs text-muted-foreground">Commission Rate</p>
                        </div>
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{eventStats.commission_budget ? eventStats.commission_budget.toLocaleString() : 'No cap'}</p>
                          <p className="text-xs text-muted-foreground">Budget Cap ({eventStats.currency})</p>
                        </div>
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold text-green-500">{eventStats.total_commission_paid?.toLocaleString() || 0}</p>
                          <p className="text-xs text-muted-foreground">Paid Out</p>
                        </div>
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold text-amber-500">{eventStats.total_commission_pending?.toLocaleString() || 0}</p>
                          <p className="text-xs text-muted-foreground">Pending</p>
                        </div>
                      </div>
                      {(!eventStats.commission_percent || eventStats.commission_percent <= 0) && (
                        <p className="text-sm text-muted-foreground mt-3 text-center">
                          No commission rate set. Edit the event to configure referral commissions.
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Invite Affiliates */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <UserPlus className="w-5 h-5" /> Invite Affiliate
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Invite users by phone or email. They'll be auto-approved and can generate referral links.
                      </p>

                      {/* Method toggle */}
                      <div className="inline-flex rounded-md border p-1 mb-3" data-testid="invite-method-toggle">
                        <button
                          type="button"
                          onClick={() => switchInviteMethod('phone')}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition ${inviteMethod === 'phone' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                          data-testid="invite-method-phone"
                        >
                          Invite by Phone
                        </button>
                        <button
                          type="button"
                          onClick={() => switchInviteMethod('email')}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition ${inviteMethod === 'email' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                          data-testid="invite-method-email"
                        >
                          Invite by Email
                        </button>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        {inviteMethod === 'phone' ? (
                          <>
                            <Select value={inviteCountryCode} onValueChange={setInviteCountryCode}>
                              <SelectTrigger className="w-[100px]" data-testid="invite-country-code">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="+234">+234</SelectItem>
                                <SelectItem value="+1">+1</SelectItem>
                                <SelectItem value="+44">+44</SelectItem>
                                <SelectItem value="+233">+233</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              placeholder="Phone number"
                              value={invitePhone}
                              onChange={(e) => setInvitePhone(e.target.value)}
                              className="flex-1"
                              data-testid="invite-phone-input"
                            />
                          </>
                        ) : (
                          <Input
                            type="email"
                            placeholder="affiliate@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            className="flex-1"
                            data-testid="invite-email-input"
                          />
                        )}
                        <Button onClick={handleInviteAffiliate} disabled={inviting} data-testid="invite-affiliate-btn">
                          {inviting ? 'Inviting...' : 'Invite'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Affiliates List */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Affiliates ({affiliates.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {affiliates.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No affiliates yet. Invite someone above.</p>
                      ) : (
                        <div className="space-y-2">
                          {affiliates.map(a => (
                            <div key={a.affiliate_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`affiliate-${a.affiliate_id}`}>
                              <div>
                                <p className="font-medium text-sm">{a.user_name || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground">{a.user_phone || a.user_email || '—'}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={a.status === 'approved' ? 'default' : 'secondary'}>
                                  {a.status}
                                </Badge>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleRemoveAffiliate(a.affiliate_id)}
                                  data-testid={`remove-affiliate-${a.affiliate_id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Event Performance */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" /> Referral Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{eventStats.total_referrers}</p>
                          <p className="text-xs text-muted-foreground">Referrers</p>
                        </div>
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{eventStats.total_clicks}</p>
                          <p className="text-xs text-muted-foreground">Clicks</p>
                        </div>
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{eventStats.total_conversions}</p>
                          <p className="text-xs text-muted-foreground">Conversions</p>
                        </div>
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{eventStats.total_revenue?.toLocaleString() || 0}</p>
                          <p className="text-xs text-muted-foreground">Revenue ({eventStats.currency})</p>
                        </div>
                      </div>

                      {eventStats.referrals?.length > 0 && (
                        <div className="border rounded-lg overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-3 font-medium">Referrer</th>
                                <th className="text-center p-3 font-medium">Code</th>
                                <th className="text-center p-3 font-medium">Clicks</th>
                                <th className="text-center p-3 font-medium">Sales</th>
                                <th className="text-right p-3 font-medium">Revenue</th>
                                <th className="text-right p-3 font-medium">Commission</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {eventStats.referrals.map(r => (
                                <tr key={r.referral_id}>
                                  <td className="p-3">{r.referrer_name || 'Unknown'}</td>
                                  <td className="p-3 text-center font-mono text-xs">{r.ref_code}</td>
                                  <td className="p-3 text-center">{r.clicks}</td>
                                  <td className="p-3 text-center">{r.conversions}</td>
                                  <td className="p-3 text-right">{(r.total_revenue || 0).toLocaleString()}</td>
                                  <td className="p-3 text-right">{(r.total_commission_earned || 0).toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Commission Payouts */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <DollarSign className="w-5 h-5" /> Commission Payouts
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {eventCommissions.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No commission records yet.</p>
                      ) : (
                        <div className="border rounded-lg overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-3 font-medium">Affiliate</th>
                                <th className="text-right p-3 font-medium">Sale</th>
                                <th className="text-center p-3 font-medium">Rate</th>
                                <th className="text-right p-3 font-medium">Commission</th>
                                <th className="text-center p-3 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {eventCommissions.map(c => (
                                <tr key={c.commission_id}>
                                  <td className="p-3">{c.affiliate_name || 'Unknown'}</td>
                                  <td className="p-3 text-right">{(c.ticket_sale_amount || 0).toLocaleString()}</td>
                                  <td className="p-3 text-center">{c.commission_percent}%</td>
                                  <td className="p-3 text-right font-medium">{(c.commission_amount || 0).toLocaleString()}</td>
                                  <td className="p-3 text-center">
                                    <Badge variant={c.status === 'paid' ? 'default' : 'outline'}>
                                      {c.status}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          )}

          {/* ===== MY REFERRALS TAB ===== */}
          <TabsContent value="affiliate" className="space-y-6">
            {isOrganizer && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Plus className="w-5 h-5" /> Create Referral Link
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                      <SelectTrigger className="flex-1" data-testid="referral-create-event-select">
                        <SelectValue placeholder="Select an event" />
                      </SelectTrigger>
                      <SelectContent>
                        {myEvents.map(e => (
                          <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={() => handleCreateLink(selectedEventId)} disabled={!selectedEventId || creating} data-testid="create-referral-btn">
                      {creating ? 'Creating...' : 'Generate Link'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle className="text-lg">My Referral Links</CardTitle></CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />)}</div>
                ) : myLinks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Link2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No referral links yet.</p>
                    <p className="text-xs mt-1">You need to be invited as an affiliate by an event organizer.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myLinks.map(link => (
                      <div
                        key={link.referral_id}
                        className={`flex items-center gap-3 p-3 border rounded-lg transition-all ${
                          highlightedRefId === link.referral_id ? 'ring-2 ring-primary shadow-lg bg-primary/5' : ''
                        }`}
                        data-testid={`referral-link-${link.ref_code}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{link.event_title}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">{link.ref_code}</span>
                            <span className="text-xs text-muted-foreground">
                              {link.clicks} clicks | {link.conversions} sales
                            </span>
                            {(link.commission_percent > 0) && (
                              <Badge variant="outline" className="text-xs">{link.commission_percent}% commission</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => copyLink(link.ref_code, link.event_id)} data-testid={`copy-link-${link.ref_code}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Link to={`/events/${link.event_id}`} target="_blank">
                            <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== MY EARNINGS TAB ===== */}
          <TabsContent value="earnings" className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-500" data-testid="earnings-total">{myTotalEarned.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Total Earned</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-500" data-testid="earnings-pending">{myTotalPending.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Pending Payout</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader><CardTitle className="text-lg">Commission History</CardTitle></CardHeader>
              <CardContent>
                {myCommissions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wallet className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No commission earnings yet.</p>
                    <p className="text-xs mt-1">Earn commissions by sharing referral links for events.</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-3 font-medium">Date</th>
                          <th className="text-right p-3 font-medium">Sale Amount</th>
                          <th className="text-center p-3 font-medium">Rate</th>
                          <th className="text-right p-3 font-medium">Commission</th>
                          <th className="text-center p-3 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {myCommissions.map(c => (
                          <tr key={c.commission_id}>
                            <td className="p-3 text-xs">
                              {new Date(c.created_at).toLocaleDateString()}
                              {c.assisted_purchase && (
                                <Badge variant="secondary" className="ml-2 text-[10px]" data-testid={`assisted-badge-${c.commission_id}`}>Assisted</Badge>
                              )}
                            </td>
                            <td className="p-3 text-right">{c.currency} {(c.ticket_sale_amount || 0).toLocaleString()}</td>
                            <td className="p-3 text-center">{c.commission_percent}%</td>
                            <td className="p-3 text-right font-medium">{c.currency} {(c.commission_amount || 0).toLocaleString()}</td>
                            <td className="p-3 text-center">
                              <Badge variant={c.status === 'paid' ? 'default' : 'outline'}>{c.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== PAYOUTS TAB ===== */}
          <TabsContent value="payouts" className="space-y-6">
            <AffiliatePayouts />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
